import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axiosClient from "../../../../api-client/AxiosClient";
import Spinner from "../../../common/Spinner/Spinner";
import { showError } from "../../../common/Utils";
import { FaArrowLeft } from "react-icons/fa";

// ── Constants ──────────────────────────────────────────────────────────────
const API_MAPPER_UPDATE_ENDPOINT =
  process.env.REACT_APP_MAPPER_UPDATE_URL ||
  "https://appservice-falconapi-dev-weu-gjf0egeae5axamau.westeurope-01.azurewebsites.net/api/Mapper/UpdateMapperFile";

const EDITABLE_FIELDS = [
  "In-scope",
  "Mandatory Field (S4)",
  "Source - Table Name",
  "Source - Field Name",
  "Default Value",
  "Comments",
  "Transformation Rule",
  "Source field (1-to-1 mapping)",
  "Cross Reference Table",
  "Conversion Rules (Logic)",
  "Reusable Logic (Y/N)",
  "Critical Error Handeling (if applicable)",
  "Data Cleansing Considerations",
  "Source - Group / View",
  "Source - Field Description",
  "Source - Data Type",
  "Source - Data Length",
  "View - Field Name",
  "Field Name (LTMC)",
  "Field Description (MC)",
  "Target Structure Description (MC)",
  "Group",
  "Type",
  "Length",
  "Decimal",
];

const COLUMN_DEFINITIONS = [
  { label: "", cls: "w-10" },
  { label: "Source", cls: "w-28" },
  { label: "Sq.No", cls: "w-12" },
  { label: "Field Description", cls: "min-w-[180px]" },
  { label: "Field Name", cls: "min-w-[120px]" },
  { label: "Target Structure", cls: "min-w-[180px]" },
  { label: "Group", cls: "w-32" },
  { label: "Mandatory", cls: "w-24" },
  { label: "In-scope", cls: "w-20" },
  { label: "Type", cls: "w-20" },
  { label: "Length", cls: "w-20" },
  { label: "Decimal", cls: "w-20" },
  { label: "Source Table", cls: "min-w-[160px]" },
  { label: "Source Field", cls: "min-w-[140px]" },
  { label: "Default Value", cls: "min-w-[120px]" },
  { label: "Comments", cls: "min-w-[140px]" },
];

const FIELD_MAPPING = [
  "Sq.No",
  "Field Description (MC)",
  "Field Name (LTMC)",
  "Target Structure Description (MC)",
  "Group",
  "Mandatory Field (S4)",
  "In-scope",
  "Type",
  "Length",
  "Decimal",
  "Source - Table Name",
  "Source - Field Name",
  "Default Value",
  "Comments",
];

const MappingComparison = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Data passed via navigate state from ImportData
  const { mismatchedRecords = [], pendingUploadResponse = null } =
    location.state || {};

  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState({});

  // Initialize: default every conflict to "Uploaded"
  const initSelectedRows = () => {
    const initial = {};
    const seen = new Set();
    mismatchedRecords.forEach((rec) => {
      if (!seen.has(rec.mapping_file_details_id)) {
        seen.add(rec.mapping_file_details_id);
        const id = rec.mapping_file_details_id;
        // Group to detect if only one side exists
        const sources = new Set(
          mismatchedRecords
            .filter((r) => r.mapping_file_details_id === id)
            .map((r) => r.Source),
        );
        if (sources.has("Uploaded") && !sources.has("Existing")) {
          initial[id] = "Uploaded"; // new-only row: selected by default
        } else if (sources.has("Existing") && !sources.has("Uploaded")) {
          initial[id] = null; // existing-only row: deselected by default
        } else {
          initial[id] = "Uploaded"; // conflict row: default to Uploaded
        }
      }
    });
    return initial;
  };

  const [selectedRows, setSelectedRows] = useState(initSelectedRows);

  // ── Group records by mapping_file_details_id ──────────────────────────────
  const grouped = {};
  mismatchedRecords.forEach((rec) => {
    const id = rec.mapping_file_details_id;
    if (!grouped[id]) grouped[id] = {};
    grouped[id][rec.Source] = rec;
  });

  const totalConflicts = Object.keys(grouped).length;
  const resolvedCount = Object.values(selectedRows).filter(Boolean).length;

  // Detect row type: "conflict" | "uploaded-only" | "existing-only"
  const getRowType = (versions) => {
    const hasExisting = !!versions["Existing"];
    const hasUploaded = !!versions["Uploaded"];
    if (hasExisting && hasUploaded) return "conflict";
    if (hasUploaded) return "uploaded-only";
    return "existing-only";
  };

  const allConflictIds = Object.keys(grouped);

  const handleSelectAll = (source) => {
    setSelectedRows((prev) => {
      const next = { ...prev };
      allConflictIds.forEach((id) => {
        const rowType = getRowType(grouped[id]);
        if (source === "Existing" && rowType === "uploaded-only") return; // can't select Existing on upload-only
        if (source === "Uploaded" && rowType === "existing-only") return; // can't select Uploaded on existing-only
        next[id] = source;
      });
      return next;
    });
  };
  // ── Helpers ───────────────────────────────────────────────────────────────
  const isDiff = (existing, uploaded, field) =>
    (existing?.[field] || "") !== (uploaded?.[field] || "");

  const selectRow = (detailsId, source) => {
    setSelectedRows((prev) => ({ ...prev, [detailsId]: source }));
  };

  const rowClass = (source, selected, rowType) => {
    const isSelected = selected === source;
    const base = "cursor-pointer transition-colors";
    // Unmatched rows get a subtle purple tint
    if (rowType === "uploaded-only" || rowType === "existing-only") {
      return isSelected
        ? `${base} bg-purple-50 border-l-4 border-l-purple-400`
        : `${base} bg-purple-50/30 hover:bg-purple-50/60 border-l-4 border-l-purple-200`;
    }
    if (source === "Existing") {
      return isSelected
        ? `${base} bg-blue-50 border-l-4 border-l-blue-500`
        : `${base} bg-white hover:bg-blue-50/40 border-l-4 border-l-transparent`;
    }
    return isSelected
      ? `${base} bg-green-50 border-l-4 border-l-green-500`
      : `${base} bg-white hover:bg-green-50/40 border-l-4 border-l-transparent`;
  };

  const handleCellEdit = (detailsId, source, field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [`${detailsId}_${source}_${field}`]: value,
    }));
  };

  const getCellValue = (rec, detailsId, source, field) => {
    const key = `${detailsId}_${source}_${field}`;
    if (editedData[key] !== undefined) {
      return editedData[key];
    }
    return rec?.[field] ?? "";
  };

  const renderCell = (
    rec,
    existing,
    uploaded,
    field,
    detailsId,
    source,
    selected,
  ) => {
    const hasDiff = isDiff(existing, uploaded, field);
    const isSelected = selected === source;
    const value = getCellValue(rec, detailsId, source, field);

    // Non-editable fields
    const readOnlyFields = ["Sq.No"];
    const isReadOnly = readOnlyFields.includes(field);

    if (isSelected && !isReadOnly) {
      return (
        <td
          key={field}
          className={`px-2 py-1.5 text-xs border-b border-gray-100 ${
            hasDiff ? "bg-orange-50" : ""
          }`}
          style={{ maxWidth: "160px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={value}
            onChange={(e) =>
              handleCellEdit(detailsId, source, field, e.target.value)
            }
            className={`w-full px-2 py-1 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-400 ${
              hasDiff
                ? "border-orange-300 bg-orange-50 text-orange-700 font-semibold focus:border-orange-400"
                : "border-gray-300 bg-white text-gray-800"
            }`}
            style={{ minWidth: "80px" }}
          />
        </td>
      );
    }

    return (
      <td
        key={field}
        className={`px-4 py-3 text-xs whitespace-nowrap border-b border-gray-100 ${
          hasDiff
            ? "text-orange-600 font-semibold bg-orange-50"
            : "text-gray-700"
        }`}
        title={value}
        style={{
          maxWidth: "160px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value || "—"}
      </td>
    );
  };

  // ── Approve handler ────────────────────────────────────────────────────────
  const buildUpdateRequest = (detailsId, source) => {
    const record = mismatchedRecords.find(
      (r) => r.mapping_file_details_id === detailsId && r.Source === source,
    );
    if (!record) return null;

    // Merge any inline edits the user made on top of the base record
    const mergedRecord = { ...record };
    EDITABLE_FIELDS.forEach((field) => {
      const key = `${detailsId}_${source}_${field}`;
      if (editedData[key] !== undefined) {
        mergedRecord[field] = editedData[key];
      }
    });

    return {
      UserMappingFileDetailsId: detailsId,
      inscope: mergedRecord["In-scope"] || "",
      mandatoryfeild: mergedRecord["Mandatory Field (S4)"] || "",
      mandatoryfield: mergedRecord["Mandatory Field (S4)"] || "",
      comments: mergedRecord["Comments"] || "",
      conversionrule: mergedRecord["Conversion Rules (Logic)"] || "",
      criticalerrorhandling:
        mergedRecord["Critical Error Handeling (if applicable)"] || "",
      crossreferencetable: mergedRecord["Cross Reference Table"] || "",
      datacleansingconsiderations:
        mergedRecord["Data Cleansing Considerations"] || "",
      decimalscale: Number(mergedRecord["Decimal"]) || 0,
      defaultvalue: mergedRecord["Default Value"] || "",
fieldname: mergedRecord["Field Name (LTMC)"] || "",
fielddescription: mergedRecord["Field Description (MC)"] || "",
length: mergedRecord["Length"] || "",
      reusablelogic: mergedRecord["Reusable Logic (Y/N)"] || "",
      sourcedatalength: mergedRecord["Source - Data Length"] || "",
      sourcedatatype: mergedRecord["Source - Data Type"] || "",
      sourcefield: mergedRecord["Source field (1-to-1 mapping)"] || "",
      sourcefielddescription:
        mergedRecord["Source - Field Description"] || "",
      sourcefieldname: mergedRecord["Source - Field Name"] || "",
      sourcegroupview: mergedRecord["Source - Group / View"] || "",
      sourcetablename: mergedRecord["Source - Table Name"] || "",
      sqno: String(mergedRecord["Sq.No"]) || "",
      targetstructure: mergedRecord["Target Structure (MC)"] || "",
      transformationrule: mergedRecord["Transformation Rule"] || "",
      type: mergedRecord["Type"] || "",
      viewfieldname: mergedRecord["View - Field Name"] || "",
    };
  };

  const handleApprove = async () => {
    if (!pendingUploadResponse) {
      toast.error("No upload response found. Please try again.");
      return;
    }

    const userMappingFileId = pendingUploadResponse.userMappingFileId;

    const updateRequests = Object.entries(selectedRows)
      .filter(([, source]) => source !== null) // skip deselected rows
      .map(([detailsId, source]) => buildUpdateRequest(detailsId, source))
      .filter(Boolean);

    try {
      setLoading(true);
      await axiosClient.post(API_MAPPER_UPDATE_ENDPOINT, {
        userMappingFileId,
        updateMapperDetailsRequests: updateRequests,
      });

      toast.success("Mapping file updated successfully!");
      navigate("/view-data", {
        state: { fileID: userMappingFileId },
      });
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // ── Guard: if navigated directly without state ────────────────────────────
  if (!pendingUploadResponse || mismatchedRecords.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-gray-500 text-sm">No conflict data found.</p>
        <button
          onClick={() => navigate("/upload/add-upload")}
          aria-label="Go back to import data"
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          <FaArrowLeft size={14} />
          Back to Import
        </button>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="flex flex-col"
      style={{
        height: "calc(100vh - 120px)",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {loading && <Spinner />}

      {/* ── Page Header ── */}
      <div
        className="flex items-center justify-between px-6 py-3.5 bg-white border-b border-gray-100 flex-shrink-0"
        style={{ boxShadow: "0 1px 3px 0 rgba(0,0,0,0.04)" }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/upload/add-upload")}
            aria-label="Go back to upload"
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-700 text-xs font-medium px-2.5 py-1.5 rounded-md hover:bg-gray-100 transition-all duration-150 border border-transparent hover:border-gray-200"
          >
            <FaArrowLeft size={11} />
            Back
          </button>
          <div className="w-px h-5 bg-gray-200" />
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2 4h5v2H2V4zm0 3h5v2H2V7zm0 3h5v2H2v-2zm7-6h5v2H9V4zm0 3h5v2H9V7zm0 3h3v2H9v-2z"
                  fill="white"
                  fillOpacity="0.9"
                />
              </svg>
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900 leading-tight">
                Review Mapping Conflicts
              </h1>
              <p className="text-xs text-gray-400 leading-tight mt-0.5">
                Select a version for each conflict, then approve to save
                changes.
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-100">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-700">
              {totalConflicts} conflict{totalConflicts === 1 ? "" : "s"}  
            </span>
          </div>
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
              resolvedCount === totalConflicts
                ? "bg-emerald-50 border-emerald-100"
                : "bg-gray-50 border-gray-100"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${resolvedCount === totalConflicts ? "bg-emerald-400" : "bg-gray-300"}`}
            />
            <span
              className={`text-xs font-semibold ${resolvedCount === totalConflicts ? "text-emerald-700" : "text-gray-500"}`}
            >
              {resolvedCount} / {totalConflicts} resolved
            </span>
          </div>
        </div>
      </div>

      {/* ── Toolbar Bar ── */}
      <div className="flex items-center justify-between px-6 py-2 bg-white border-b border-gray-100 flex-shrink-0">
        {/* Legend pills */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 font-medium mr-1">
            Legend:
          </span>
          {[
            {
              color: "bg-blue-500",
              bg: "bg-blue-50",
              text: "text-blue-700",
              label: "Existing",
            },
            {
              color: "bg-emerald-500",
              bg: "bg-emerald-50",
              text: "text-emerald-700",
              label: "New Upload",
            },
            {
              color: "bg-amber-400",
              bg: "bg-amber-50",
              text: "text-amber-700",
              label: "Changed value",
            },
            {
              color: "bg-violet-400",
              bg: "bg-violet-50",
              text: "text-violet-700",
              label: "One-side only",
            },
          ].map(({ color, bg, text, label }) => (
            <span
              key={label}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${bg} text-xs font-medium ${text}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${color} flex-shrink-0`} />
              {label}
            </span>
          ))}
        </div>

        {/* Divider + Select all */}
        <div className="flex items-center gap-2">
          <div className="w-px h-4 bg-gray-200 mr-1" />
          <span className="text-xs text-gray-400 font-medium">Select all:</span>
          <button
            onClick={() => handleSelectAll("Existing")}
            aria-label="Select all existing version rows"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold text-blue-700 bg-white border border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-150"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            <span>Existing</span>
          </button>
          <button
            onClick={() => handleSelectAll("Uploaded")}
            aria-label="Select all new upload version rows"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold text-emerald-700 bg-white border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-150"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span>New Upload</span>
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div
        style={{ flex: 1, overflow: "auto", minHeight: 0, overflowX: "auto" }}
      >
        <table className="min-w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50 border-b border-gray-200">
              {COLUMN_DEFINITIONS.map(({ label, cls }) => (
                <th
                  key={label}
                  className={`px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest ${cls} whitespace-nowrap`}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Object.entries(grouped).map(([detailsId, versions], groupIdx) => {
              const existing = versions["Existing"];
              const uploaded = versions["Uploaded"];
              const selected = selectedRows[detailsId];

              return (
                <React.Fragment key={detailsId}>
                  {/* Dashed separator between conflict groups */}
                  {groupIdx > 0 && (
                    <tr>
                      <td colSpan={16} className="p-0">
                        <div className="border-t-2 border-dashed border-gray-200" />
                      </td>
                    </tr>
                  )}

                  {/* Existing row */}
                  {existing && (
                    <tr
                      className={rowClass(
                        "Existing",
                        selected,
                        getRowType(versions),
                      )}
                      onClick={() => selectRow(detailsId, "Existing")}
                    >
                      <td className="px-4 py-3 border-b border-gray-100">
                        {getRowType(versions) === "existing-only" ? (
                          <input
                            type="checkbox"
                            checked={selected === "Existing"}
                            onChange={(e) =>
                              e.target.checked
                                ? selectRow(detailsId, "Existing")
                                : setSelectedRows((prev) => ({
                                    ...prev,
                                    [detailsId]: null,
                                  }))
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="accent-blue-600 w-4 h-4 cursor-pointer"
                            title="Include this existing-only row"
                          />
                        ) : (
                          <input
                            type="radio"
                            name={`conflict-${detailsId}`}
                            checked={selected === "Existing"}
                            onChange={() => selectRow(detailsId, "Existing")}
                            onClick={(e) => e.stopPropagation()}
                            className="accent-blue-600 w-4 h-4 cursor-pointer"
                          />
                        )}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-50">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-600 border border-blue-100 whitespace-nowrap">
                          <span className="w-1 h-1 rounded-full bg-blue-500 flex-shrink-0" />
                          <span>Existing</span>
                        </span>
                      </td>
                      {/* Existing row data cells */}
                      {FIELD_MAPPING.map((field) =>
                        renderCell(
                          existing,
                          existing,
                          uploaded,
                          field,
                          detailsId,
                          "Existing",
                          selected,
                        ),
                      )}
                    </tr>
                  )}

                  {/* Uploaded row */}
                  {uploaded && (
                    <tr
                      className={rowClass(
                        "Uploaded",
                        selected,
                        getRowType(versions),
                      )}
                      onClick={() => selectRow(detailsId, "Uploaded")}
                    >
                      <td className="px-4 py-3 border-b border-gray-100">
                        {getRowType(versions) === "uploaded-only" ? (
                          <input
                            type="checkbox"
                            checked={selected === "Uploaded"}
                            onChange={(e) =>
                              e.target.checked
                                ? selectRow(detailsId, "Uploaded")
                                : setSelectedRows((prev) => ({
                                    ...prev,
                                    [detailsId]: null,
                                  }))
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="accent-green-600 w-4 h-4 cursor-pointer"
                            title="Include this new-only row"
                          />
                        ) : (
                          <input
                            type="radio"
                            name={`conflict-${detailsId}`}
                            checked={selected === "Uploaded"}
                            onChange={() => selectRow(detailsId, "Uploaded")}
                            onClick={(e) => e.stopPropagation()}
                            className="accent-green-600 w-4 h-4 cursor-pointer"
                          />
                        )}
                      </td>
                      <td className="px-4 py-2.5 border-b border-gray-50">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100 whitespace-nowrap">
                          <span className="w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
                          <span>New Upload</span>
                        </span>
                      </td>
                      {/* Uploaded row data cells */}
                      {FIELD_MAPPING.map((field) =>
                        renderCell(
                          uploaded,
                          existing,
                          uploaded,
                          field,
                          detailsId,
                          "Uploaded",
                          selected,
                        ),
                      )}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Footer / Action Bar ── */}
      <div
        className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-100 flex-shrink-0"
        style={{
          minHeight: "56px",
          boxShadow: "0 -1px 3px 0 rgba(0,0,0,0.04)",
        }}
      >
        {/* Progress indicator */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="relative w-28 h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full rounded-full transition-all duration-300"
                style={{
                  width: `${totalConflicts > 0 ? (resolvedCount / totalConflicts) * 100 : 0}%`,
                  background:
                    resolvedCount === totalConflicts ? "#10b981" : "#3b82f6",
                }}
              />
            </div>
            <span className="text-xs text-gray-400 tabular-nums">
              <span className="font-semibold text-gray-700">
                {resolvedCount}
              </span>
              <span> / {totalConflicts}</span>
            </span>
          </div>
          {resolvedCount < totalConflicts && (
            <span className="text-xs text-amber-600 font-medium">
              {totalConflicts - resolvedCount} unresolved
            </span>
          )}
          {resolvedCount === totalConflicts && (
            <span className="text-xs text-emerald-600 font-medium">
              All resolved
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/upload/add-upload")}
            aria-label="Cancel and go back to upload"
            className="px-4 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
          >
            Cancel
          </button>
          <button
            onClick={handleApprove}
            disabled={loading}
            aria-label="Approve and save mapping changes"
            className="inline-flex items-center gap-2 px-5 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
          >
            {loading ? (
              <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
            ) : (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 6.5L4.5 9L10 3"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            Approve &amp; Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default MappingComparison;
